import { cn } from '@/lib/utils';

export interface RecencyBadgeProps {
  /** Days since the property was listed (created_at). Negatives clamped to 0. */
  daysSince: number;
  className?: string;
}

/**
 * `<RecencyBadge>` — pill showing how recently a property was listed.
 *
 * Per UI-SPEC § 3.10 + § 8 + § 1.1.1 (bronze reserved-for list):
 * - Tier 1 (≤7d):    `bg-bronze-500 text-ocean-950`  reading `"New · {N}d"`
 * - Tier 2 (8–30d):  `bg-foam-50/15 text-foam-50`    reading `"{N}d"`
 * - Tier 3 (31–90d): `bg-foam-50/10 text-foam-50/70` reading `"{N}d"`
 * - Tier 4 (>90d):   `bg-foam-50/5 text-foam-50/50`  reading `"{N}d"`
 *
 * Bronze accent is reserved for tier 1 (one of the 6 places bronze appears
 * per UI-SPEC § 1.1.1). No animation. Static badge.
 */
export function RecencyBadge({ daysSince, className }: RecencyBadgeProps) {
  const days = Math.max(0, Math.floor(daysSince));
  let tone: string;
  let label: string;
  if (days <= 7) {
    tone = 'bg-bronze-500 text-ocean-950';
    label = `New · ${days}d`;
  } else if (days <= 30) {
    tone = 'bg-foam-50/15 text-foam-50';
    label = `${days}d`;
  } else if (days <= 90) {
    tone = 'bg-foam-50/10 text-foam-50/70';
    label = `${days}d`;
  } else {
    tone = 'bg-foam-50/5 text-foam-50/50';
    label = `${days}d`;
  }
  return (
    <span
      className={cn(
        'inline-flex h-6 px-2 items-center rounded-full text-xs font-semibold whitespace-nowrap',
        tone,
        className,
      )}
      aria-label={`Listed ${days} days ago`}
    >
      {label}
    </span>
  );
}
